import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import { Globe, ShoppingCart, Code2, Smartphone, TrendingUp, Wrench, ArrowRight } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { PageTitle } from "../components/Shared";
import Seo from "../components/Seo";

const content = {
  it: {
    title: "Servizi e soluzioni su misura",
    seoDesc: "Servizi web IdeaMedia: sviluppo siti web, e-commerce, software e moduli personalizzati, web app, SEO e assistenza informatica per aziende.",
    intro: "Un aspetto molto importante della nostra azienda \u00e8 il continuo studio e sviluppo di soluzioni innovative per le aziende: dalla creazione di gestionali su misura all'implementazione di funzioni particolari in siti web e app, il tutto all'insegna della semplicit\u00e0 di utilizzo e della funzionalit\u00e0.",
    services: [
      { icon: Globe, title: "Sviluppo siti web", text: "Siti web professionali, responsive e ottimizzati: dalla soluzione Basic per piccole attivit\u00e0 fino al sito Professional da 20 pagine con grafica personalizzata.", link: "/shop?cat=sviluppo-web-low-cost", linkLabel: "Vedi le offerte" },
      { icon: ShoppingCart, title: "E-commerce", text: "Negozi online completi su misura: cataloghi, pagamenti, spedizioni, multinegozio e integrazioni con gestionali come Danea Easyfatt e piattaforme Prestashop.", link: "/portfolio?cat=e-commerce", linkLabel: "Esempi realizzati" },
      { icon: Code2, title: "Software e moduli personalizzati", text: "Moduli e plugin custom per Prestashop e Perfex CRM, connettori Zapier, automazioni workflow e gestionali personalizzabili per fatture, preventivi e clienti.", link: "/shop", linkLabel: "Vai allo shop" },
      { icon: Smartphone, title: "Sviluppo APP e web app", text: "Applicazioni web personalizzate e PWA: piattaforme delivery multinegozio, condivisione diagnosi mediche DICOM, portali con abbonamento e molto altro.", link: "/portfolio?cat=app-e-web-app", linkLabel: "Progetti realizzati" },
      { icon: TrendingUp, title: "SEO e statistiche", text: "Posizionamento sui motori di ricerca con risultati concreti, piattaforma proprietaria di monitoraggio SEO e statistiche per i siti con servizio professionale incluso.", link: "/servizi/local-seo-pro", linkLabel: "Scopri LOCAL SEO PRO" },
      { icon: Wrench, title: "Assistenza informatica", text: "Supporto tecnico PC e software con assistenza remota, disponibili dal luned\u00ec al venerd\u00ec in orario d'ufficio (9.30-18.30). Supporto orario anche per API, CRM e workflow.", link: "/contatti", linkLabel: "Contattaci" },
    ],
    ctaTitle: "Hai un progetto in mente?",
    ctaText: "Raccontaci la tua idea: troveremo insieme la soluzione pi\u00f9 semplice ed efficace.",
    ctaBtn: "Contattaci ora",
  },
  en: {
    title: "Tailor-made services and solutions",
    seoDesc: "IdeaMedia web services: website development, e-commerce, custom software and modules, web apps, SEO and IT support for businesses.",
    intro: "A core aspect of our company is the continuous research and development of innovative solutions for businesses: from custom management systems to special features for websites and apps, always focused on ease of use and functionality.",
    services: [
      { icon: Globe, title: "Website development", text: "Professional, responsive and optimized websites: from the Basic solution for small businesses up to the 20-page Professional website with custom graphics.", link: "/shop?cat=sviluppo-web-low-cost", linkLabel: "See the offers" },
      { icon: ShoppingCart, title: "E-commerce", text: "Complete tailor-made online stores: catalogs, payments, shipping, multi-store setups and integrations with management systems like Danea Easyfatt and Prestashop platforms.", link: "/portfolio?cat=e-commerce", linkLabel: "Completed examples" },
      { icon: Code2, title: "Custom software & modules", text: "Custom modules and plugins for Prestashop and Perfex CRM, Zapier connectors, workflow automation and customizable management systems for invoices, quotes and clients.", link: "/shop", linkLabel: "Go to shop" },
      { icon: Smartphone, title: "APP & web app development", text: "Custom web applications and PWAs: multi-store delivery platforms, DICOM medical imaging sharing, subscription-based portals and much more.", link: "/portfolio?cat=app-e-web-app", linkLabel: "Completed projects" },
      { icon: TrendingUp, title: "SEO & statistics", text: "Search engine positioning with concrete results, proprietary SEO monitoring platform and statistics for websites with the professional service included.", link: "/servizi/local-seo-pro", linkLabel: "Discover LOCAL SEO PRO" },
      { icon: Wrench, title: "IT support", text: "PC and software technical support with remote assistance, available Monday to Friday during office hours (9.30am-6.30pm). Hourly support also for APIs, CRMs and workflows.", link: "/contatti", linkLabel: "Contact us" },
    ],
    ctaTitle: "Have a project in mind?",
    ctaText: "Tell us your idea: together we will find the simplest and most effective solution.",
    ctaBtn: "Contact us now",
  },
};

const Servizi = () => {
  const { lang } = useShop();
  const { lp } = useLocale();
  const c = content[lang];
  const location = useLocation();

  // anchor ids matching the sidebar submenu (original site subpages)
  const anchors = ["sviluppo-web", "ecommerce", "software", "app", "seo", "assistenza"];

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      }
    }
  }, [location.hash]);

  return (
    <div data-testid="services-page">
      <Seo title={c.title} description={c.seoDesc} />
      <PageTitle title={c.title} />

      <section className="idm-section">
        <div className="idm-container">
          <p className="idm-text idm-narrow idm-center-text">{c.intro}</p>
          <div className="idm-services-grid">
            {c.services.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div className="idm-service-card" id={anchors[idx]} key={s.title} data-testid="service-card">
                  <Icon size={32} strokeWidth={1.4} />
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <Link to={lp(s.link)} className="idm-service-link">
                    {s.linkLabel} <ArrowRight size={13} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="idm-section idm-section-gray">
        <div className="idm-container idm-center">
          <h2 className="idm-section-title">{c.ctaTitle}</h2>
          <span className="idm-separator" />
          <p className="idm-text idm-center-text">{c.ctaText}</p>
          <Link to={lp("/contatti")} className="idm-btn-yellow" style={{ animation: "none", marginTop: 18 }} data-testid="services-cta">
            {c.ctaBtn}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Servizi;
